<template>
  <div class="retention-defaults-product">
    <retention-tabs />

    <div class="page-header">
      <div class="header-text">
        <a class="back-link" @click="goBack">
          <i class="fas fa-chevron-left"></i> Back to Default Messages
        </a>
        <h2>
          Defaults for <code>{{ productId }}</code>
        </h2>
        <p class="hint">
          {{ configuredCount }} / {{ supportedLocales.length }} locales configured
          <span v-if="distinctMessageCount > 0"> · {{ distinctMessageCount }} distinct {{ distinctMessageCount === 1 ? 'message' : 'messages' }}</span>
        </p>
      </div>
      <span v-if="!canConfigureRetentionDefaultMessages" class="permission-note">
        No permission to configure defaults
      </span>
    </div>

    <div v-if="isLoading" class="empty">Loading…</div>

    <template v-else>
      <div class="toolbar">
        <div class="toolbar-row">
          <span class="toolbar-label">Filter:</span>
          <button
            class="filter-btn"
            :class="{ active: filter === 'all' }"
            @click="filter = 'all'"
          >All ({{ supportedLocales.length }})</button>
          <button
            class="filter-btn"
            :class="{ active: filter === 'configured' }"
            @click="filter = 'configured'"
          >Configured ({{ configuredCount }})</button>
          <button
            class="filter-btn"
            :class="{ active: filter === 'unconfigured' }"
            @click="filter = 'unconfigured'"
          >Unconfigured ({{ supportedLocales.length - configuredCount }})</button>
          <span class="vertical-divider"></span>
          <span class="toolbar-label">Assigned Message:</span>
          <multiselect
            v-model="filterMessageOption"
            class="bulk-select filter-ms"
            :options="distinctAssignedMessages"
            :custom-label="messageLabel"
            track-by="id"
            placeholder="— any —"
            :show-labels="false"
            :allow-empty="true"
            :max-height="320"
          />
        </div>
        <div class="toolbar-row">
          <span class="toolbar-label">Search:</span>
          <div class="search-input-wrap">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="localeSearch"
              type="text"
              class="locale-search-input"
              placeholder="Filter locale (e.g. de, English)"
            />
            <i
              v-if="localeSearch"
              class="fas fa-times clear-icon"
              @click="localeSearch = ''"
            ></i>
          </div>
        </div>
        <div class="toolbar-row">
          <span class="toolbar-label">Selection:</span>
          <button class="link-btn" @click="selectAllVisible">Select all visible</button>
          <button class="link-btn" @click="selectNone">Select none</button>
          <button class="link-btn" @click="selectUnconfigured">Select all unconfigured</button>
          <span class="selection-summary">
            {{ selectedLocales.size }} selected
          </span>
        </div>
      </div>

      <div class="table-wrap">
        <table class="locales-table">
          <thead>
            <tr>
              <th class="col-check">
                <input
                  type="checkbox"
                  :checked="allVisibleSelected"
                  :indeterminate.prop="someVisibleSelected"
                  @change="toggleAllVisible($event)"
                />
              </th>
              <th class="col-locale">Locale</th>
              <th class="col-code">Code</th>
              <th class="col-message">Assigned Message</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(loc, idx) in visibleLocales"
              :key="loc.code"
              class="data-row"
              :class="{ selected: selectedLocales.has(loc.code) }"
              @click="onRowClick(loc, idx, $event)"
            >
              <td class="col-check" @click.stop>
                <input
                  type="checkbox"
                  :checked="selectedLocales.has(loc.code)"
                  @change="toggleLocale(loc.code, idx, $event)"
                />
              </td>
              <td>{{ loc.label }}</td>
              <td><code class="loc-code">{{ loc.code }}</code></td>
              <td>
                <template v-if="assignmentFor(loc.code)">
                  <span class="msg-header">{{ messageNameFor(assignmentFor(loc.code).messageId) }}</span>
                  <span
                    class="state-pill"
                    :class="`state-${(messageStateFor(assignmentFor(loc.code).messageId) || 'missing').toLowerCase()}`"
                  >
                    {{ messageStateFor(assignmentFor(loc.code).messageId) || 'missing' }}
                  </span>
                </template>
                <span v-else class="muted">—</span>
              </td>
            </tr>
            <tr v-if="visibleLocales.length === 0">
              <td colspan="4" class="empty-row">No locales match the current filters.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- Sticky bulk-action bar -->
    <div
      v-if="selectedLocales.size > 0 && !bulkRunning && canConfigureRetentionDefaultMessages"
      class="bulk-bar"
    >
      <span class="bulk-count">
        <strong>{{ selectedLocales.size }}</strong>
        {{ selectedLocales.size === 1 ? 'locale' : 'locales' }} selected
      </span>
      <div class="bulk-mode">
        <button class="filter-btn" :class="{ active: bulkMode === 'message' }" @click="bulkMode = 'message'">Same message</button>
        <button class="filter-btn" :class="{ active: bulkMode === 'group' }" @click="bulkMode = 'group'">From group</button>
      </div>
      <template v-if="bulkMode === 'message'">
        <span class="bulk-label">Assign to:</span>
        <multiselect
          v-model="bulkSelectedMessage"
          class="bulk-select"
          :options="approvedMessages"
          :custom-label="messageLabel"
          track-by="messageIdentifier"
          placeholder="— pick a message —"
          :show-labels="false"
          :allow-empty="true"
          :max-height="400"
        />
        <button class="btn btn-primary" @click="apply" :disabled="!bulkMessageIdentifier">Apply</button>
      </template>
      <template v-else>
        <span class="bulk-label">Group:</span>
        <multiselect
          v-model="bulkSelectedGroup"
          class="bulk-select"
          :options="selectableGroups"
          :custom-label="groupLabel"
          track-by="groupId"
          placeholder="— pick a group —"
          :show-labels="false"
          :allow-empty="true"
          :max-height="400"
        />
        <button class="btn btn-primary" @click="apply" :disabled="!groupApplyReady">Apply</button>
      </template>
      <div class="bulk-bar-right">
        <button
          v-if="canDeleteRetentionDefaultMessages"
          class="btn btn-remove-default"
          @click="clearAssignments"
        >
          Remove Default Message Assignment
        </button>
      </div>
      <p v-if="bulkMode === 'message' && approvedMessages.length === 0" class="bulk-hint">
        No APPROVED messages available.
      </p>
      <p v-else-if="bulkMode === 'group' && bulkGroupId" class="bulk-hint">
        {{ groupMatchInfo.matched.length }} of {{ selectedLocales.size }} selected locale(s) have an approved message in this group;
        {{ groupMatchInfo.skipped.length }} will be skipped.
      </p>
    </div>

    <retention-bulk-runner-modals
      unit="locale"
      :running="bulkRunning"
      :progress="bulkProgress"
      :show-result="showResultModal"
      :result="bulkResult"
      :running-verb="bulkAction === 'configure' ? 'Assigning' : 'Clearing'"
      :complete-title="bulkAction === 'configure' ? 'Bulk assign' : 'Bulk clear'"
      :retryable-failures="retryableFailures"
      :terminal-failures="invalidProductFailures"
      terminal-label="rejected by Apple"
      @retry="retryFailed"
      @close="closeResultModal"
    >
      <template #terminal>
        <h4><i class="fas fa-info-circle"></i> Rejected by Apple — not retryable</h4>
        <p class="info-text">
          Apple doesn't recognize <code>{{ productId }}</code> as a valid product ID for retention defaults. The product has been removed from the retention products list and these {{ invalidProductFailures.length }} locale assignment(s) won't be retried.
        </p>
      </template>
    </retention-bulk-runner-modals>
  </div>
</template>

<script>
import RetentionTabs from './Tabs.vue';
import RetentionBulkRunnerModals from '../../components/Retention/RetentionBulkRunnerModals.vue';
import { permissionsMixin } from '../../mixins/permissions.mixin';
import { retentionBulkRunnerMixin } from '../../mixins/retentionBulkRunner.mixin';
import { SUPPORTED_LOCALES } from '../../constants/retentionLocales';
import notify from '../../util/notification';

export default {
  mixins: [permissionsMixin, retentionBulkRunnerMixin],
  components: { RetentionTabs, RetentionBulkRunnerModals },
  data() {
    return {
      supportedLocales: SUPPORTED_LOCALES,
      filter: 'all',
      filterMessageId: null,
      localeSearch: '',
      selectedLocales: new Set(),
      lastClickedIndex: null,
      bulkMode: 'message',
      bulkMessageIdentifier: null,
      bulkGroupId: null,
      bulkAction: 'configure',
      bulkLastMessageIdentifier: null,
      bulkLastGroupId: null
    };
  },
  computed: {
    productId() {
      return this.$route.params.productId;
    },
    defaults() {
      return this.$store.getters['retention/defaults'] || [];
    },
    productDefaults() {
      return this.defaults.filter(d => d.productId === this.productId);
    },
    retentionMessages() {
      return this.$store.getters.retentionMessages || [];
    },
    approvedMessages() {
      return this.$store.getters['retention/defaultEligibleMessages'] || [];
    },
    groups() {
      return this.$store.getters['retention/groups'] || [];
    },
    // Groups with at least one approved, language-bearing member (hides orphan/draft-only ones).
    selectableGroups() {
      return this.groups.filter(g => this.membersByLocaleFor(g.groupId).size > 0);
    },
    bulkSelectedGroup: {
      get() {
        return this.groups.find(g => g.groupId === this.bulkGroupId) || null;
      },
      set(g) {
        this.bulkGroupId = g ? g.groupId : null;
      }
    },
    // locale -> APPROVED group member, for the currently-selected group.
    groupMembersByLocale() {
      return this.membersByLocaleFor(this.bulkGroupId);
    },
    groupMatchInfo() {
      const all = Array.from(this.selectedLocales);
      const map = this.groupMembersByLocale;
      return {
        matched: all.filter(l => map.has(l)),
        skipped: all.filter(l => !map.has(l))
      };
    },
    groupApplyReady() {
      return !!this.bulkGroupId && this.groupMatchInfo.matched.length > 0;
    },
    bulkSelectedMessage: {
      get() {
        return (
          this.approvedMessages.find(
            m => m.messageIdentifier === this.bulkMessageIdentifier
          ) || null
        );
      },
      set(m) {
        this.bulkMessageIdentifier = m ? m.messageIdentifier : null;
      }
    },
    filterMessageOption: {
      get() {
        return this.distinctAssignedMessages.find(m => m.id === this.filterMessageId) || null;
      },
      set(m) {
        this.filterMessageId = m ? m.id : null;
      }
    },
    isLoading() {
      return this.$store.getters['retention/isLoadingDefaults'];
    },
    assignmentByLocale() {
      const map = new Map();
      for (const d of this.productDefaults) map.set(d.locale, d);
      return map;
    },
    configuredCount() {
      // Count only supported locales that have an assignment. Orphaned defaults for
      // unsupported/legacy locales would otherwise push this above the locale total
      // and yield a negative "unconfigured".
      return this.supportedLocales.filter(l => this.assignmentByLocale.has(l.code)).length;
    },
    distinctMessageCount() {
      const ids = new Set(this.productDefaults.map(d => d.messageId));
      return ids.size;
    },
    distinctAssignedMessages() {
      const ids = new Set(this.productDefaults.map(d => d.messageId));
      return this.retentionMessages.filter(m => ids.has(m.id));
    },
    visibleLocales() {
      let list = this.supportedLocales.slice();
      if (this.filter === 'configured') {
        list = list.filter(l => this.assignmentByLocale.has(l.code));
      } else if (this.filter === 'unconfigured') {
        list = list.filter(l => !this.assignmentByLocale.has(l.code));
      }
      if (this.filterMessageId) {
        list = list.filter(l => {
          const a = this.assignmentByLocale.get(l.code);
          return a && a.messageId === this.filterMessageId;
        });
      }
      const q = this.localeSearch.trim().toLowerCase();
      if (q) {
        list = list.filter(l =>
          l.code.toLowerCase().includes(q) ||
          (l.label || '').toLowerCase().includes(q)
        );
      }
      return list;
    },
    allVisibleSelected() {
      return (
        this.visibleLocales.length > 0 &&
        this.visibleLocales.every(l => this.selectedLocales.has(l.code))
      );
    },
    someVisibleSelected() {
      const some = this.visibleLocales.some(l => this.selectedLocales.has(l.code));
      return some && !this.allVisibleSelected;
    }
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch('retention/fetchDefaults'),
      this.$store.dispatch('retention/fetchProducts'),
      this.$store.dispatch('retention/fetchMessages'),
      this.$store.dispatch('retention/fetchGroups')
    ]);
  },
  beforeRouteLeave(to, from, next) {
    if (this.bulkRunning) {
      const ok = confirm('A bulk operation is still running. Leave anyway?');
      if (!ok) return next(false);
    }
    next();
  },
  methods: {
    goBack() {
      this.$router.push({
        name: 'RetentionDefaultsTab',
        params: { aid: this.$store.getters.activeApp }
      });
    },
    assignmentFor(code) {
      return this.assignmentByLocale.get(code) || null;
    },
    messageNameFor(messageId) {
      const m = this.retentionMessages.find(msg => msg.id === messageId);
      return m?.name || m?.header || '(message not found)';
    },
    messageStateFor(messageId) {
      const m = this.retentionMessages.find(msg => msg.id === messageId);
      return m?.messageState || null;
    },
    messageLabel(m) {
      return m.name || m.header || '(unnamed)';
    },
    groupLabel(g) {
      const count = (this.$store.getters['retention/messagesByGroupId'][g.groupId] || []).length;
      return `${g.name || '(unnamed group)'} · ${count} message${count === 1 ? '' : 's'}`;
    },
    onRowClick(loc, idx, event) {
      // Click anywhere on the row toggles its selection. Shift-click extends a range.
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'A') return;
      this.toggleLocale(loc.code, idx, event);
    },
    toggleLocale(code, idx, event) {
      // Range select on shift+click
      if (event && event.shiftKey && this.lastClickedIndex !== null) {
        const [from, to] = [this.lastClickedIndex, idx].sort((a, b) => a - b);
        const target = !this.selectedLocales.has(code);
        const next = new Set(this.selectedLocales);
        for (let i = from; i <= to; i++) {
          const c = this.visibleLocales[i]?.code;
          if (!c) continue;
          if (target) next.add(c);
          else next.delete(c);
        }
        this.selectedLocales = next;
      } else {
        const next = new Set(this.selectedLocales);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        this.selectedLocales = next;
      }
      this.lastClickedIndex = idx;
    },
    toggleAllVisible(event) {
      const target = event.target.checked;
      const next = new Set(this.selectedLocales);
      for (const l of this.visibleLocales) {
        if (target) next.add(l.code);
        else next.delete(l.code);
      }
      this.selectedLocales = next;
    },
    selectAllVisible() {
      const next = new Set(this.selectedLocales);
      for (const l of this.visibleLocales) next.add(l.code);
      this.selectedLocales = next;
    },
    selectNone() {
      this.selectedLocales = new Set();
      this.lastClickedIndex = null;
    },
    selectUnconfigured() {
      const next = new Set(this.selectedLocales);
      for (const l of this.supportedLocales) {
        if (!this.assignmentByLocale.has(l.code)) next.add(l.code);
      }
      this.selectedLocales = next;
    },
    async apply() {
      if (this.bulkMode === 'group') return this.applyGroup();
      if (!this.bulkMessageIdentifier || this.selectedLocales.size === 0) return;
      const locales = Array.from(this.selectedLocales);
      this.bulkLastMessageIdentifier = this.bulkMessageIdentifier;
      await this.runBulk({
        locales,
        messageIdentifier: this.bulkMessageIdentifier,
        action: 'configure'
      });
    },
    // locale -> approved member. Method (not computed) so retries can resolve
    // against the group that ran, not the current picker selection.
    membersByLocaleFor(groupId) {
      const map = new Map();
      if (!groupId) return map;
      for (const m of this.approvedMessages) {
        if (m.groupId === groupId && m.language) map.set(m.language, m);
      }
      return map;
    },
    async applyGroup() {
      const { matched, skipped } = this.groupMatchInfo;
      if (matched.length === 0) {
        notify('None of the selected locales have an approved message in this group.', 'info');
        return;
      }
      await this.runBulkGroup({ locales: matched, skipped, groupId: this.bulkGroupId });
    },
    // Assign each locale its same-language group member; unmatched locales are skipped.
    async runBulkGroup({ locales, skipped, groupId }) {
      this.bulkAction = 'configure';
      this.bulkLastMessageIdentifier = null;
      this.bulkLastGroupId = groupId;
      const productId = this.productId;
      const map = this.membersByLocaleFor(groupId);
      const worker = async (locale) => {
        const member = map.get(locale);
        if (!member) {
          // Member gone/unapproved since planning - terminal, not retryable.
          throw { response: { status: 400, data: { message: 'No approved message for this locale in the group anymore' } } };
        }
        await this.$store.dispatch('retention/configureDefault', {
          productId, locale, messageIdentifier: member.messageIdentifier, silent: true
        });
      };

      await this.executeBulk({ items: locales, worker });

      const failed = this.bulkResult.failures.length;
      const ok = this.bulkResult.successes.length;
      notify(
        failed === 0
          ? `Bulk assigned: ${ok} of ${locales.length} succeeded.`
          : `Bulk assigned: ${ok} succeeded, ${failed} failed.`,
        failed === 0 ? 'success' : 'error'
      );
      if (skipped && skipped.length) {
        notify(
          `${skipped.length} locale(s) had no approved message in this group and were skipped: ${skipped.join(', ')}.`,
          'info'
        );
      }
      if (failed === 0) {
        const next = new Set(this.selectedLocales);
        for (const l of this.bulkResult.successes) next.delete(l);
        this.selectedLocales = next;
      }

      await this.refetchIfProductInvalid();
    },
    async clearAssignments() {
      if (this.selectedLocales.size === 0) return;
      const onlyConfigured = Array.from(this.selectedLocales).filter(c =>
        this.assignmentByLocale.has(c)
      );
      if (onlyConfigured.length === 0) {
        notify('None of the selected locales are configured.', 'info');
        return;
      }
      if (!confirm(`Clear ${onlyConfigured.length} default assignment(s)? Underlying messages stay intact.`)) {
        return;
      }
      this.bulkLastMessageIdentifier = null;
      await this.runBulk({
        locales: onlyConfigured,
        messageIdentifier: null,
        action: 'remove'
      });
    },
    async retryFailed() {
      // Only retry the retryable bucket — if the productId itself was rejected as
      // invalid (4000023) every locale would just hit the same wall.
      const locales = this.retryableFailures.map(f => f.item);
      if (locales.length === 0) return;
      this.showResultModal = false;
      // Group mode retries against the group that ran (snapshotted), not the current pick.
      if (this.bulkMode === 'group' && this.bulkAction === 'configure') {
        await this.runBulkGroup({ locales, skipped: [], groupId: this.bulkLastGroupId });
        return;
      }
      await this.runBulk({
        locales,
        messageIdentifier: this.bulkLastMessageIdentifier,
        action: this.bulkAction
      });
    },
    async runBulk({ locales, messageIdentifier, action }) {
      this.bulkAction = action;
      const productId = this.productId;
      const worker = async (locale) => {
        if (action === 'configure') {
          await this.$store.dispatch('retention/configureDefault', {
            productId, locale, messageIdentifier, silent: true
          });
        } else {
          await this.$store.dispatch('retention/removeDefault', {
            productId, locale, silent: true
          });
        }
      };

      await this.executeBulk({ items: locales, worker });

      const failed = this.bulkResult.failures.length;
      const ok = this.bulkResult.successes.length;
      const verb = action === 'configure' ? 'assigned' : 'cleared';
      if (failed === 0) {
        notify(`Bulk ${verb}: ${ok} of ${locales.length} succeeded.`, 'success');
        if (action === 'configure') {
          this.selectNone();
          this.bulkMessageIdentifier = null;
        } else {
          const next = new Set(this.selectedLocales);
          for (const l of this.bulkResult.successes) next.delete(l);
          this.selectedLocales = next;
        }
      } else {
        notify(`Bulk ${verb}: ${ok} succeeded, ${failed} failed.`, 'error');
      }

      await this.refetchIfProductInvalid();
    },
    // Apple flagged the product invalid (4000023) and it was blacklisted server-side.
    async refetchIfProductInvalid() {
      if (this.invalidProductFailures.length === 0) return;
      await this.$store.dispatch('retention/fetchProducts');
      notify(
        `Product "${this.productId}" flagged invalid by Apple, and removed from the retention catalog.`,
        'info'
      );
    },
    closeResultModal() {
      this.closeBulkResultModal();
    }
  }
};
</script>

<style scoped>
.retention-defaults-product {
  flex: 1;
  min-width: 0;
  padding: 0.5rem 0.25rem 6rem;
  color: rgba(255, 255, 255, 0.88);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.header-text h2 {
  margin: 0.3rem 0 0.3rem;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
}

.header-text h2 code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 1.05rem;
  background: rgba(255, 255, 255, 0.06);
  padding: 2px 10px;
  border-radius: 5px;
  color: #fff;
}

.back-link {
  color: #03B3DA;
  cursor: pointer;
  text-decoration: none;
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.back-link:hover { text-decoration: underline; }

.hint {
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  margin: 0;
}

.permission-note {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
}

.empty {
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}

.toolbar {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
  margin-bottom: 0.6rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.toolbar-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.toolbar-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.filter-btn {
  padding: 4px 11px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.78rem;
  cursor: pointer;
  transition: background-color 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.filter-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.filter-btn.active {
  background: #03B3DA;
  border-color: #03B3DA;
  color: #fff;
}

.bulk-select {
  color: #fff;
  font-size: 0.85rem;
}

/* vue-multiselect dark theme overrides (scoped -> ::v-deep) */
.bulk-select ::v-deep .multiselect__tags {
  min-height: 36px;
  padding: 6px 40px 0 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
}
.bulk-select ::v-deep .multiselect__single,
.bulk-select ::v-deep .multiselect__input {
  background: transparent;
  color: #fff;
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding: 0;
}
.bulk-select ::v-deep .multiselect__placeholder {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
  margin-bottom: 6px;
  padding-top: 0;
}
.bulk-select ::v-deep .multiselect--active .multiselect__tags {
  border-color: #03B3DA;
  box-shadow: 0 0 0 0.15rem rgba(3, 179, 218, 0.2);
}
.bulk-select ::v-deep .multiselect__content-wrapper {
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.bulk-select ::v-deep .multiselect__option {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  white-space: normal;
  min-height: unset;
  padding: 8px 12px;
}
.bulk-select ::v-deep .multiselect__option--highlight,
.bulk-select ::v-deep .multiselect__option--highlight::after {
  background: #03B3DA;
  color: #fff;
}
.bulk-select ::v-deep .multiselect__option--selected {
  background: rgba(3, 179, 218, 0.18);
  color: #fff;
  font-weight: 600;
}
.bulk-select ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #03B3DA;
}

.vertical-divider {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.12);
  margin: 0 0.3rem;
}

.search-input-wrap {
  position: relative;
  flex: 1;
  max-width: 320px;
}

.search-input-wrap .search-icon {
  position: absolute;
  left: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.35);
  font-size: 0.78rem;
  pointer-events: none;
}

.locale-search-input {
  width: 100%;
  padding: 0.4rem 1.85rem 0.4rem 1.9rem;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.85rem;
}

.locale-search-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.locale-search-input:focus {
  outline: none;
  border-color: #03B3DA;
  background: rgba(255, 255, 255, 0.06);
  box-shadow: 0 0 0 3px rgba(3, 179, 218, 0.15);
}

.search-input-wrap .clear-icon {
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.78rem;
  cursor: pointer;
}

.search-input-wrap .clear-icon:hover {
  color: #fff;
}

.link-btn {
  background: none;
  border: none;
  color: #03B3DA;
  font-size: 0.82rem;
  cursor: pointer;
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
}

.link-btn:hover { background: rgba(3, 179, 218, 0.1); }

.selection-summary {
  margin-left: auto;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
}

.table-wrap {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
}

.locales-table {
  width: 100%;
  border-collapse: collapse;
  color: rgba(255, 255, 255, 0.85);
}

.locales-table th,
.locales-table td {
  text-align: left;
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.88rem;
  vertical-align: middle;
}

.locales-table thead th {
  background: rgba(255, 255, 255, 0.04);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.locales-table tr.data-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.locales-table tr.data-row:hover { background: rgba(3, 179, 218, 0.06); }
.locales-table tr.data-row.selected { background: rgba(3, 179, 218, 0.12); }
.locales-table tr.data-row.selected:hover { background: rgba(3, 179, 218, 0.18); }

.locales-table tr:last-child td { border-bottom: none; }

.locales-table input[type="checkbox"] {
  cursor: pointer;
  accent-color: #03B3DA;
}

.col-check { width: 36px; text-align: center; }
.col-locale { width: 30%; }
.col-code { width: 110px; }
.col-message { width: auto; }

.loc-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.55);
  background: none;
}

.msg-header {
  display: inline-block;
  margin-right: 0.5rem;
  color: rgba(255, 255, 255, 0.95);
}

.state-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.state-draft     { background: rgba(255, 255, 255, 0.1);  color: #c8d0d9; }
.state-pending   { background: rgba(255, 192, 50, 0.15);  color: #ffc832; }
.state-approved  { background: rgba(46, 204, 113, 0.18);  color: #2ecc71; }
.state-rejected  { background: rgba(231, 76, 60, 0.18);   color: #e74c3c; }
.state-missing,
.state-unknown   { background: rgba(255, 255, 255, 0.07); color: rgba(255, 255, 255, 0.5); }

.muted { color: rgba(255, 255, 255, 0.35); }

.empty-row {
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  padding: 1.5rem;
  font-style: italic;
}

/* Sticky bulk action bar */
.bulk-bar {
  position: fixed;
  bottom: 0;
  left: 260px;
  right: 0;
  background: rgba(20, 25, 38, 0.96);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 14px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(6px);
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 200;
  flex-wrap: wrap;
}

.bulk-count {
  font-size: 0.92rem;
  color: rgba(255, 255, 255, 0.85);
}
.bulk-count strong { color: #03B3DA; }

.bulk-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.bulk-mode {
  display: inline-flex;
  gap: 0.35rem;
}

.bulk-select {
  width: 340px;
  min-width: 340px;
}
/* The Assigned-Message filter multiselect. Wide enough to read long message names;
   options wrap (multiselect__option: white-space normal) so nothing is truncated. */
.filter-ms {
  width: 360px;
  min-width: 360px;
}
.filter-ms ::v-deep .multiselect__single {
  white-space: normal;
  line-height: 1.3;
}

.bulk-hint {
  width: 100%;
  margin: 0.4rem 0 0;
  font-size: 0.8rem;
  color: #ffb84d;
}

.btn {
  padding: 0.45rem 1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.btn-primary {
  background: #03B3DA;
  color: #fff;
}
.btn-primary:hover { background: #04a3c5; }
.btn-primary:disabled {
  background: rgba(3, 179, 218, 0.35);
  color: rgba(255, 255, 255, 0.6);
  cursor: not-allowed;
}
/* Pushes the destructive action to the far right, away from Apply. */
.bulk-bar-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
/* Custom danger button — avoids the global template's .btn-danger gradient that
   washed out the red label. Solid red on hover keeps the text readable. */
.btn-remove-default {
  background: rgba(231, 76, 60, 0.12);
  color: #ff6b5e;
  border: 1px solid rgba(231, 76, 60, 0.5);
}
.btn-remove-default:hover {
  background: #e74c3c;
  color: #fff;
  border-color: #e74c3c;
}
</style>
